import { useRef, useEffect, useCallback } from 'react';
import { draggable, dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { TableListItemProps } from 'common/types/common';

interface UseDndProps {
  onUpdateItemFolder?: (itemId: string, folderId: string) => Promise<void>;
  onRemoveItemFromFolder?: (itemId: string, folderId: string) => Promise<void>;
  items: TableListItemProps[];
  itemDataKey: 'chartId' | 'dashboardId';
  unfolderedAreaRef: React.RefObject<HTMLElement | null>;
}

export const useTableDragAndDrop = ({ onUpdateItemFolder, onRemoveItemFromFolder, items, itemDataKey, unfolderedAreaRef }: UseDndProps) => {
  const folderDropCleanups = useRef<Map<string, () => void>>(new Map());
  const draggableCleanups = useRef<Map<string, () => void>>(new Map());

  const setupFolderDropTarget = useCallback(
    (folderId: string, element: HTMLElement | null) => {
      const existing = folderDropCleanups.current.get(folderId);
      if (existing) existing();

      if (element && onUpdateItemFolder) {
        const cleanup = dropTargetForElements({
          element,
          onDrop: ({ source }) => {
            const itemId = source.data?.[itemDataKey];
            if (typeof itemId === 'string') onUpdateItemFolder(itemId, folderId);
          },
          onDragEnter: () => element.classList.add('drop-target'),
          onDragLeave: () => element.classList.remove('drop-target'),
        });
        folderDropCleanups.current.set(folderId, cleanup);
      }
    },
    [onUpdateItemFolder, itemDataKey],
  );

  const setupDraggable = useCallback(
    (itemId: string, element: HTMLElement | null) => {
      if (!element) return;
      const existing = draggableCleanups.current.get(itemId);
      if (existing) existing();

      const cleanup = draggable({
        element,
        getInitialData: () => ({ [itemDataKey]: itemId }),
      });
      draggableCleanups.current.set(itemId, cleanup);
    },
    [itemDataKey],
  );

  useEffect(() => {
    const folderDropCleanupCopy = folderDropCleanups.current;
    const draggableCleanupsCopy = draggableCleanups.current;

    return () => {
      folderDropCleanupCopy.forEach(cleanup => cleanup());
      draggableCleanupsCopy.forEach(cleanup => cleanup());
    };
  }, []);

  useEffect(() => {
    const element = unfolderedAreaRef.current;
    if (!element || !onRemoveItemFromFolder) return;

    return dropTargetForElements({
      element,
      onDrop: ({ source }) => {
        const itemId = source.data?.[itemDataKey];
        if (typeof itemId === 'string') {
          const item = items.find(i => i.uuid === itemId);
          item?.folders?.forEach(f => {
            onRemoveItemFromFolder(itemId, f.id);
          });
        }
      },
    });
  }, [onRemoveItemFromFolder, items, itemDataKey, unfolderedAreaRef]);

  return { setupFolderDropTarget, setupDraggable };
};
